'use client'

import type { AbstractIntlMessages } from 'next-intl'
import { type ReactNode, use } from 'react'

import { useAppLocale } from '@/components/i18n/locale-provider'
import type { Locale } from '@/lib/i18n/config'
import {
  type IslandNamespace,
  islandMessagesPath,
} from '@/lib/i18n/island-namespaces'
import { MessagesProvider } from './messages-provider'
import { sharedCatalogLoad } from './shared-catalog-load'

export type { IslandNamespace } from '@/lib/i18n/island-namespaces'

/**
 * One fetch per locale and namespace, shared by every island that asks: the
 * home page's islands list `emails`, `chat` and `settings` several times
 * over.
 */
const catalogLoads = new Map<string, Promise<AbstractIntlMessages>>()

/**
 * One promise per locale and namespace list: `use` must get the same one
 * back on every render of an island.
 */
const listLoads = new Map<string, Promise<AbstractIntlMessages>>()

/**
 * One island catalog, English already merged under the locale by the
 * server (`app/i18n/islands/[locale]/[namespace]/route.ts`). Fetched, not
 * imported: a client `import()` per locale and namespace made each catalog
 * an async chunk of every route that shows an island.
 */
function loadCatalog(
  locale: Locale,
  namespace: IslandNamespace
): Promise<AbstractIntlMessages> {
  const path = islandMessagesPath(locale, namespace)
  return sharedCatalogLoad(catalogLoads, path, async () => {
    const response = await fetch(path)
    if (!response.ok) {
      throw new Error(
        `Island catalog ${locale}/${namespace} answered ${response.status}`
      )
    }
    return (await response.json()) as AbstractIntlMessages
  })
}

function loadMessages(
  locale: Locale,
  namespaces: ReadonlyArray<IslandNamespace>
): Promise<AbstractIntlMessages> {
  return sharedCatalogLoad(listLoads, `${locale}:${namespaces.join(',')}`, () =>
    Promise.all(
      namespaces.map(
        async (namespace) =>
          [namespace, await loadCatalog(locale, namespace)] as const
      )
    ).then((entries) => Object.fromEntries(entries) as AbstractIntlMessages)
  )
}

/**
 * Catalogs for a lazy client island a marketing page embeds (the home
 * hero's product demo, the feature scenes): they load with the island, as
 * JSON, instead of riding the page's payload, so the page carries only its
 * own copy. Suspends while they load, so render it inside the island's
 * Suspense boundary; islands mount only in the browser.
 */
export function LazyMessages({
  namespaces,
  children,
}: {
  namespaces: ReadonlyArray<IslandNamespace>
  children: ReactNode
}) {
  const locale = useAppLocale()
  const messages = use(loadMessages(locale, namespaces))
  return (
    <MessagesProvider locale={locale} messages={messages}>
      {children}
    </MessagesProvider>
  )
}
